import React from 'react';
import { useLocation } from 'react-router-dom';
import { AppContext } from '../contexts/app-context';
import { AuthContext } from '../contexts/auth-context';
import { useAllianceIdentity } from '../hooks/use-alliance-identity';
import { useSessionActions } from '../hooks/use-session-actions';
import { AllianceAppBar } from './alliance/alliance-app-bar';
import { ConnectionBanner, UpdatedBanner } from './alliance/app-banners';
import { SIDEBAR_WIDTH } from './alliance/app-nav-drawer';
import { SessionExpiredDialog } from './alliance/session-expired-dialog';
import { UserControl } from './alliance/user-control';
import { GettingStarted, useShowGettingStarted } from './getting-started';

/**
 * Contracting's App Bar: page title, Getting started (actions slot), user
 * control (with RecruitLink) and the App Launcher. Rendered once by the shell
 * (components/dashboard.tsx), and by a full-width RouteContainer that shows
 * outside the shell (the Inactive agent's Wallet), which has no sidebar.
 *
 * Also mounts what every signed-in page needs once: the connection and
 * "updated" banner sources (shown by <AppBanners /> under the bar) and the
 * session-expired dialog.
 *
 * @param sidebar - Offset the bar for the permanent sidebar at md+ and show
 *   the menu button below md
 */
export function AppShellBar(props: { sidebar: boolean }) {
  // Context
  const { state, dispatch } = React.useContext(AppContext);
  const authContext = React.useContext(AuthContext);
  // Hooks
  const location = useLocation();
  const identity = useAllianceIdentity();
  const { signOut, stopImpersonating, signInAgain } = useSessionActions();
  const showGettingStarted = useShowGettingStarted({
    enabled: props.sidebar,
  });

  return (
    <React.Fragment>
      <AllianceAppBar
        title={state.routeTitle}
        sidebarWidth={props.sidebar ? SIDEBAR_WIDTH : undefined}
        loading={state.routeLoading}
        onMenuClick={
          props.sidebar ? () => dispatch({ type: 'TOGGLE_DRAWER' }) : undefined
        }
        menuOpen={state.drawerOpen}
        actions={
          // Not on full-width pages (the Inactive agent's Wallet only)
          props.sidebar && showGettingStarted ? <GettingStarted /> : undefined
        }
        user={
          identity ? (
            <UserControl
              identity={identity}
              onSignOut={signOut}
              onStopImpersonating={stopImpersonating}
            />
          ) : undefined
        }
      />

      {/* Banner sources: each keeps its banner in the queue while it applies */}
      <ConnectionBanner />
      <UpdatedBanner pathname={location.pathname} />

      <SessionExpiredDialog
        open={authContext.state.tokenError}
        onSignIn={signInAgain}
        onSignOut={signOut}
      />
    </React.Fragment>
  );
}
